import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { companiesApi } from "@/api/companies";
import { apiFetch } from "@/api/client";
import { knowledgeApi } from "@/api/knowledge";
import { tenantUsersApi, type CreateTenantUserInput } from "@/api/tenant-users";
import { tenantOnboardApi, type OnboardResult, type OnboardUser } from "@/api/tenant-onboard";
import { queryKeys } from "@/lib/query-keys";
import {
  ArrowLeft, ArrowRight, Check, Building2, Cloud,
  Shield, Globe, KeyRound, Users, Trash2, Plus, CheckCircle2, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ProvisioningPhase = "company" | "resource_group" | "network" | "complete" | "failed";
type Step = 1 | 2 | 3 | 4 | 5 | 6;
type AuthProvider = "entra_id" | "google" | "local";
type Tier = "standard" | "premium" | "enterprise";

interface ManualUser {
  email: string;
  displayName: string;
  role: "admin" | "manager" | "viewer";
}

const TIERS: { id: Tier; label: string; desc: string }[] = [
  { id: "standard", label: "Standard", desc: "Core platform features" },
  { id: "premium", label: "Premium", desc: "Advanced integrations & priority support" },
  { id: "enterprise", label: "Enterprise", desc: "Full platform, dedicated resources, SLA" },
];

const AUTH_PROVIDERS: { id: AuthProvider; label: string; desc: string; icon: typeof Shield }[] = [
  { id: "entra_id", label: "Microsoft Entra ID", desc: "Connect to customer's Azure AD for SSO", icon: Shield },
  { id: "google", label: "Google Workspace", desc: "Connect to customer's Google for SSO", icon: Globe },
  { id: "local", label: "Local Only", desc: "Email/password accounts managed in the platform", icon: KeyRound },
];

function generatePrefix(name: string): string {
  const words = name.trim().split(/\s+/);
  let prefix: string;
  if (words.length >= 2) {
    prefix = words.map((w) => w[0]).join("").slice(0, 4);
  } else {
    prefix = name.slice(0, 4);
  }
  return prefix.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6) || "TEN";
}

export function NewTenant() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [provisioningPhase, setProvisioningPhase] = useState<ProvisioningPhase | null>(null);
  const [provisioningError, setProvisioningError] = useState("");
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null);

  // Step 1: Company details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tier, setTier] = useState<Tier>("standard");
  const [issuePrefix, setIssuePrefix] = useState("");
  const [prefixEdited, setPrefixEdited] = useState(false);
  const [brandColor, setBrandColor] = useState("#6366f1");
  const [azureRegion, setAzureRegion] = useState("uksouth");

  // Step 2: Company Context
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [companyContext, setCompanyContext] = useState("");

  // Step 3: Auth provider
  const [authProvider, setAuthProvider] = useState<AuthProvider>("local");

  // Step 4: Provider config
  const [entraConfig, setEntraConfig] = useState({
    tenantId: "",
    clientId: "",
    clientSecret: "",
  });
  const [googleConfig, setGoogleConfig] = useState({
    clientId: "",
    clientSecret: "",
  });

  // Step 4 (Entra): Onboard OAuth state
  const [entraOnboardResult, setEntraOnboardResult] = useState<OnboardResult | null>(null);
  const [entraConnecting, setEntraConnecting] = useState(false);
  const [entraOnboardError, setEntraOnboardError] = useState("");

  // Step 5: Initial users
  const [userMode, setUserMode] = useState<"import" | "manual">("manual");
  const [manualUsers, setManualUsers] = useState<ManualUser[]>([
    { email: "", displayName: "", role: "admin" },
  ]);

  // Restore wizard state from sessionStorage (survives OAuth redirect to new tab)
  useEffect(() => {
    const saved = sessionStorage.getItem("proclaw_new_tenant");
    if (saved) {
      try {
        const s = JSON.parse(saved);
        if (s.name) setName(s.name);
        if (s.description) setDescription(s.description);
        if (s.tier) setTier(s.tier);
        if (s.issuePrefix) { setIssuePrefix(s.issuePrefix); setPrefixEdited(true); }
        if (s.brandColor) setBrandColor(s.brandColor);
        if (s.azureRegion) setAzureRegion(s.azureRegion);
        if (s.websiteUrl) setWebsiteUrl(s.websiteUrl);
        if (s.companyContext) setCompanyContext(s.companyContext);
        if (s.authProvider) setAuthProvider(s.authProvider);
      } catch { /* ignore */ }
    }
  }, []);

  // Save wizard state to sessionStorage whenever it changes
  useEffect(() => {
    const prefix = prefixEdited ? issuePrefix : generatePrefix(name);
    sessionStorage.setItem("proclaw_new_tenant", JSON.stringify({
      name, description, tier, issuePrefix: prefix, brandColor, azureRegion, websiteUrl, companyContext, authProvider,
    }));
  }, [name, description, tier, issuePrefix, prefixEdited, brandColor, azureRegion, websiteUrl, companyContext, authProvider]);

  // Detect onboardComplete query param after OAuth callback redirect
  useEffect(() => {
    const token = searchParams.get("onboardComplete");
    const onboardError = searchParams.get("onboardError");
    if (token) {
      searchParams.delete("onboardComplete");
      searchParams.delete("userFetchError");
      setSearchParams(searchParams, { replace: true });

      setEntraConnecting(true);
      tenantOnboardApi
        .getResult(token)
        .then((result) => {
          setEntraOnboardResult(result);
          setEntraConfig((prev) => ({ ...prev, tenantId: result.tenantId }));
          if (result.users.length > 0) {
            setManualUsers(
              result.users.map((u) => ({
                email: u.email,
                displayName: u.displayName,
                role: "viewer" as const,
              })),
            );
            setUserMode("manual");
          }
          setAuthProvider("entra_id");
          setStep(4);
        })
        .catch((err) => {
          setEntraOnboardError(
            err instanceof Error ? err.message : "Failed to fetch onboard result",
          );
        })
        .finally(() => {
          setEntraConnecting(false);
          // Clean up saved state after successful onboard
          sessionStorage.removeItem("proclaw_new_tenant");
        });
    } else if (onboardError) {
      searchParams.delete("onboardError");
      setSearchParams(searchParams, { replace: true });
      setEntraOnboardError(decodeURIComponent(onboardError));
      setAuthProvider("entra_id");
      setStep(4);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const effectivePrefix = prefixEdited ? issuePrefix : generatePrefix(name);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!prefixEdited) {
      setIssuePrefix(generatePrefix(value));
    }
  };

  const addManualUser = () => {
    setManualUsers((prev) => [...prev, { email: "", displayName: "", role: "viewer" }]);
  };

  const removeManualUser = (index: number) => {
    setManualUsers((prev) => prev.filter((_, i) => i !== index));
  };

  const updateManualUser = (index: number, field: keyof ManualUser, value: string) => {
    setManualUsers((prev) =>
      prev.map((u, i) => (i === index ? { ...u, [field]: value } : u)),
    );
  };

  const totalSteps = authProvider === "local" ? 4 : 6;
  const stepLabels: Record<number, string> = {
    1: "Company",
    2: "Company Context",
    3: "Auth Provider",
    4: authProvider === "local" ? "Review" : "Provider Config",
    5: "Initial Users",
    6: "Review",
  };

  const hasAdminUser = manualUsers.some(
    (u) => u.role === "admin" && u.email.trim().length > 0,
  );

  const canProceed = () => {
    if (step === 1) return name.trim().length > 0 && effectivePrefix.length >= 2;
    if (step === 2) return true; // Company Context is optional
    if (step === 3) return !!authProvider;
    if (step === 4 && authProvider === "entra_id") {
      // If connected via OAuth, tenant ID is enough
      if (entraOnboardResult) return true;
      return entraConfig.tenantId.trim().length > 0 && entraConfig.clientId.trim().length > 0;
    }
    if (step === 4 && authProvider === "google") {
      return googleConfig.clientId.trim().length > 0;
    }
    // Enforce at least one admin user on the users step (step 5 for OAuth providers)
    if (step === 5 && authProvider !== "local") {
      return hasAdminUser;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 3 && authProvider === "local") {
      // Skip steps 4 and 5 for local auth — jump to review step which is step 4 for local
      setStep(4 as Step);
      return;
    }
    setStep((step + 1) as Step);
  };

  const handleBack = () => {
    if (step === 4 && authProvider === "local") {
      setStep(3);
      return;
    }
    setStep((step - 1) as Step);
  };

  const isReviewStep =
    (authProvider === "local" && step === 4) ||
    (authProvider !== "local" && step === 6);

  const displaySteps = authProvider === "local" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5, 6];

  const handleCreate = async () => {
    setIsCreating(true);
    setCreateError("");
    setProvisioningPhase("company");
    setProvisioningError("");

    try {
      // 1. Create company
      const company = await companiesApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        tier,
        issuePrefix: effectivePrefix,
        ...(brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) ? { brandColor } : {}),
      });
      setCreatedCompanyId(company.id);

      // 2. If auth provider selected, create tenant auth config
      if (authProvider === "entra_id") {
        const resolvedTenantId = entraOnboardResult?.tenantId || entraConfig.tenantId;
        await fetch(`/api/companies/${company.id}/auth-providers/entra`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled: true,
            providerConfig: {
              tenantId: resolvedTenantId,
              clientId: entraConfig.clientId || undefined,
              clientSecret: entraConfig.clientSecret || undefined,
            },
          }),
        });
      } else if (authProvider === "google") {
        await fetch(`/api/companies/${company.id}/auth-providers/google`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled: true,
            providerConfig: {
              clientId: googleConfig.clientId,
              clientSecret: googleConfig.clientSecret || undefined,
            },
          }),
        });
      }

      // 3. If website URL or company context provided, create knowledge entries
      if (websiteUrl.trim()) {
        try {
          await knowledgeApi.create(company.id, {
            type: "website_scrape",
            name: `Website: ${websiteUrl.trim()}`,
            url: websiteUrl.trim(),
          });
        } catch {
          // Non-fatal: website scraping failure should not block tenant creation
        }
      }
      if (companyContext.trim()) {
        try {
          await knowledgeApi.create(company.id, {
            type: "document",
            name: "Company Context",
            content: companyContext.trim(),
          });
        } catch {
          // Non-fatal
        }
      }

      // 4. If manual users added, create them
      if (authProvider !== "local" && userMode === "manual") {
        const validUsers = manualUsers.filter((u) => u.email.trim());
        for (const user of validUsers) {
          const input: CreateTenantUserInput = {
            email: user.email.trim(),
            displayName: user.displayName.trim() || undefined,
            role: user.role,
            authProvider: authProvider === "entra_id" ? "entra_id" : "google",
          };
          await tenantUsersApi.create(company.id, input);
        }
      }

      // 5. Create onboarding record (pending, so customer admin sees the wizard)
      try {
        await apiFetch(`/companies/${company.id}/onboarding/step/1`, {
          method: "PUT",
          body: JSON.stringify({}),
        });
      } catch {
        // Non-fatal: onboarding row creation failure shouldn't block tenant creation
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      sessionStorage.removeItem("proclaw_new_tenant");

      // 4. Provision Azure infrastructure
      setProvisioningPhase("resource_group");
      try {
        setProvisioningPhase("network");
        const result = await companiesApi.provisionAzure(company.id, azureRegion);

        if (result.status === "ready") {
          setProvisioningPhase("complete");
          // Brief pause to show the complete state
          await new Promise((r) => setTimeout(r, 1200));
          navigate(`/tenants/${company.id}/dashboard`);
        } else if (result.status === "failed") {
          setProvisioningPhase("failed");
          setProvisioningError(result.error || "Azure provisioning failed");
        }
      } catch (provisionErr) {
        // Azure provisioning failed but company was created — allow navigating
        setProvisioningPhase("failed");
        setProvisioningError(
          provisionErr instanceof Error ? provisionErr.message : "Azure provisioning failed",
        );
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create tenant");
      setProvisioningPhase(null);
    } finally {
      setIsCreating(false);
    }
  };

  // Provisioning screen
  if (provisioningPhase) {
    const phases = [
      { key: "company", label: "Company created" },
      { key: "resource_group", label: "Provisioning Azure resource group" },
      { key: "network", label: "Setting up network isolation" },
      { key: "complete", label: "All done" },
    ] as const;

    const phaseOrder = ["company", "resource_group", "network", "complete"] as const;
    const currentIdx = phaseOrder.indexOf(provisioningPhase === "failed" ? "network" : provisioningPhase);

    return (
      <CenterPanel title="New Tenant">
        <div className="flex flex-col items-center justify-center py-16">
          {provisioningPhase !== "failed" && provisioningPhase !== "complete" && (
            <Loader2 size={48} className="mb-6 text-accent animate-spin" />
          )}
          {provisioningPhase === "complete" && (
            <CheckCircle2 size={48} className="mb-6 text-green-500" />
          )}
          {provisioningPhase === "failed" && (
            <Cloud size={48} className="mb-6 text-red-500" />
          )}

          <h2 className="mb-2 text-lg font-semibold text-text-primary">
            {provisioningPhase === "complete"
              ? "All set!"
              : provisioningPhase === "failed"
                ? "Provisioning issue"
                : `Getting things ready for ${name}...`}
          </h2>

          {provisioningPhase === "failed" && (
            <p className="mb-4 text-sm text-text-muted text-center max-w-md">
              Your tenant was created, but Azure infrastructure provisioning encountered an issue. You can provision later from the tenant settings.
            </p>
          )}

          <div className="mt-6 space-y-3 w-full max-w-sm">
            {phases.map((phase, idx) => {
              const isComplete = idx < currentIdx || provisioningPhase === "complete";
              const isCurrent = idx === currentIdx && provisioningPhase !== "complete" && provisioningPhase !== "failed";
              const isFailed = provisioningPhase === "failed" && idx === currentIdx;
              const isPending = idx > currentIdx;

              return (
                <div key={phase.key} className="flex items-center gap-3">
                  {isComplete && <Check size={16} className="text-green-500 shrink-0" />}
                  {isCurrent && <Loader2 size={16} className="text-accent animate-spin shrink-0" />}
                  {isFailed && <span className="text-red-500 shrink-0 text-sm">!</span>}
                  {isPending && <span className="h-4 w-4 rounded-full border border-border shrink-0" />}
                  <span
                    className={cn(
                      "text-sm",
                      isComplete ? "text-text-primary" : isCurrent ? "text-accent font-medium" : isFailed ? "text-red-500" : "text-text-muted",
                    )}
                  >
                    {phase.label}
                    {isFailed && provisioningError ? ` — ${provisioningError}` : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {provisioningPhase === "failed" && createdCompanyId && (
            <div className="mt-8 flex gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setProvisioningPhase("resource_group");
                  setProvisioningError("");
                  companiesApi.provisionAzure(createdCompanyId).then((result) => {
                    if (result.status === "ready") {
                      setProvisioningPhase("complete");
                      setTimeout(() => navigate(`/tenants/${createdCompanyId}/dashboard`), 1200);
                    } else {
                      setProvisioningPhase("failed");
                      setProvisioningError(result.error || "Provisioning failed");
                    }
                  }).catch((err) => {
                    setProvisioningPhase("failed");
                    setProvisioningError(err instanceof Error ? err.message : "Provisioning failed");
                  });
                }}
              >
                Retry Provisioning
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/tenants/${createdCompanyId}/dashboard`)}
              >
                Continue to Dashboard
              </Button>
            </div>
          )}
        </div>
      </CenterPanel>
    );
  }

  return (
    <CenterPanel title="New Tenant">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate("/tenants")}
      >
        <ArrowLeft size={14} className="mr-1.5" />
        Back to Tenants
      </Button>

      {/* Step indicator */}
      <div className="mb-6 flex items-center gap-2">
        {displaySteps.map((s) => (
          <div key={s} className="flex items-center gap-2">
            <button
              onClick={() => s < step && setStep(s as Step)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                s === step
                  ? "bg-accent text-white"
                  : s < step
                    ? "bg-accent/20 text-accent cursor-pointer"
                    : "bg-surface-hover text-text-muted"
              }`}
            >
              {s < step ? <Check size={14} /> : s}
            </button>
            {s < displaySteps[displaySteps.length - 1] && (
              <div className={`h-0.5 w-8 ${s < step ? "bg-accent/40" : "bg-border"}`} />
            )}
          </div>
        ))}
        <span className="ml-3 text-sm text-text-secondary">
          {isReviewStep ? "Review" : stepLabels[step]}
        </span>
      </div>

      {/* Step 1: Company Details */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Company Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Company Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g. Acme Corporation"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">Description</label>
              <textarea
                placeholder="Brief description of the company..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm min-h-[80px] resize-y"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">Tier</label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {TIERS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTier(t.id)}
                    className={`flex flex-col items-start rounded-lg border p-3 text-left transition-colors ${
                      tier === t.id
                        ? "border-accent bg-accent/5"
                        : "border-border hover:border-text-muted"
                    }`}
                  >
                    <span className="text-sm font-medium text-text-primary">{t.label}</span>
                    <span className="text-xs text-text-muted">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                  Issue Prefix (2-6 chars)
                </label>
                <Input
                  placeholder="e.g. ACME"
                  value={effectivePrefix}
                  maxLength={6}
                  onChange={(e) => {
                    setPrefixEdited(true);
                    setIssuePrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""));
                  }}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Brand Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="#6366f1"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Azure Region
              </label>
              <select
                value={azureRegion}
                onChange={(e) => setAzureRegion(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
              >
                <option value="uksouth">UK South (London)</option>
                <option value="ukwest">UK West (Wales)</option>
                <option value="westeurope">West Europe (Netherlands)</option>
                <option value="northeurope">North Europe (Ireland)</option>
                <option value="eastus">East US (Virginia)</option>
                <option value="eastus2">East US 2 (Virginia)</option>
                <option value="westus2">West US 2 (Washington)</option>
              </select>
              <p className="mt-1 text-[11px] text-text-muted">
                Region for this tenant's VMs, VNet, and resource group. Cannot be changed after provisioning.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Company Context */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Company Context</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Website URL
              </label>
              <Input
                placeholder="e.g. https://www.acme.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
              <p className="mt-1 text-xs text-text-muted">
                We'll capture key information from your website to help AI agents understand your company.
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Additional Company Context
              </label>
              <textarea
                placeholder="Tell us about your company, services, industry, key priorities..."
                value={companyContext}
                onChange={(e) => setCompanyContext(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm min-h-[120px] resize-y"
              />
              <p className="mt-1 text-xs text-text-muted">
                This information helps AI agents understand your company and provide better service.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Authentication Provider */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Authentication Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {AUTH_PROVIDERS.map((ap) => {
                const Icon = ap.icon;
                const isSelected = authProvider === ap.id;
                return (
                  <button
                    key={ap.id}
                    onClick={() => setAuthProvider(ap.id)}
                    className={`flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors ${
                      isSelected
                        ? "border-accent bg-accent/5"
                        : "border-border hover:border-text-muted"
                    }`}
                  >
                    <Icon size={24} className={isSelected ? "text-accent" : "text-text-muted"} />
                    <span className="text-sm font-medium text-text-primary">{ap.label}</span>
                    <p className="text-xs text-text-muted">{ap.desc}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Provider Configuration (skipped for local) */}
      {step === 4 && authProvider === "entra_id" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Microsoft Entra ID Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {entraOnboardResult ? (
              /* Connected state */
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/5 p-4">
                  <CheckCircle2 size={24} className="text-green-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Connected to Microsoft 365
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      Tenant ID:{" "}
                      <code className="rounded bg-background px-1 py-0.5 font-mono text-accent">
                        {entraOnboardResult.tenantId}
                      </code>
                    </p>
                  </div>
                </div>
                {entraOnboardResult.users.length > 0 && (
                  <div className="rounded-lg bg-surface-hover p-3">
                    <p className="text-sm text-text-secondary">
                      <Users size={14} className="inline mr-1.5 -mt-0.5" />
                      {entraOnboardResult.users.length} user(s) imported from directory.
                      They will be pre-populated in the next step.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Not connected state */
              <div className="space-y-4">
                <div className="flex flex-col items-center gap-4 rounded-lg border border-border p-8">
                  <Shield size={40} className="text-text-muted" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-text-primary">
                      Connect to the customer's Microsoft 365 tenant
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      The customer's admin will grant consent for this platform to read their directory.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    disabled={entraConnecting}
                    onClick={async () => {
                      setEntraConnecting(true);
                      setEntraOnboardError("");
                      try {
                        const { consentUrl } = await tenantOnboardApi.startOnboard();
                        window.open(consentUrl, "_blank");
                      } catch (err) {
                        setEntraOnboardError(
                          err instanceof Error ? err.message : "Failed to start onboarding",
                        );
                      } finally {
                        setEntraConnecting(false);
                      }
                    }}
                  >
                    {entraConnecting ? (
                      <Loader2 size={14} className="mr-1.5 animate-spin" />
                    ) : (
                      <Shield size={14} className="mr-1.5" />
                    )}
                    Connect to Microsoft 365
                  </Button>
                </div>

                {entraOnboardError && (
                  <p className="text-sm text-red-500">{entraOnboardError}</p>
                )}

                <div className="rounded-lg bg-surface-hover p-3">
                  <p className="text-xs text-text-muted">
                    A new tab will open for the customer admin to sign in and grant consent.
                    Once complete, you will be redirected back here with the tenant information.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 4 && authProvider === "google" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Google Workspace Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                Client ID <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g. 123456789.apps.googleusercontent.com"
                value={googleConfig.clientId}
                onChange={(e) =>
                  setGoogleConfig((prev) => ({ ...prev, clientId: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-secondary">Client Secret</label>
              <Input
                type="password"
                placeholder="Client secret value"
                value={googleConfig.clientSecret}
                onChange={(e) =>
                  setGoogleConfig((prev) => ({ ...prev, clientSecret: e.target.value }))
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Initial Users (skipped for local) */}
      {step === 5 && authProvider !== "local" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Initial Users</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                onClick={() => setUserMode("import")}
                className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors ${
                  userMode === "import"
                    ? "border-accent bg-accent/5"
                    : "border-border hover:border-text-muted"
                }`}
              >
                <Users size={20} className={userMode === "import" ? "text-accent" : "text-text-muted"} />
                <span className="mt-2 text-sm font-medium text-text-primary">Import from directory</span>
                <p className="text-xs text-text-muted">Sync users from identity provider</p>
              </button>
              <button
                onClick={() => setUserMode("manual")}
                className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors ${
                  userMode === "manual"
                    ? "border-accent bg-accent/5"
                    : "border-border hover:border-text-muted"
                }`}
              >
                <Plus size={20} className={userMode === "manual" ? "text-accent" : "text-text-muted"} />
                <span className="mt-2 text-sm font-medium text-text-primary">Add manually</span>
                <p className="text-xs text-text-muted">Enter user details below</p>
              </button>
            </div>

            {userMode === "import" && !entraOnboardResult && (
              <div className="rounded-lg bg-surface-hover p-4 text-center">
                <Users size={32} className="mx-auto mb-2 text-text-muted" />
                <p className="text-sm text-text-secondary">
                  Directory import via Graph API will be available in a future release.
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  For now, add users manually or configure SSO and let users self-register.
                </p>
              </div>
            )}

            {userMode === "manual" && (
              <div className="space-y-3">
                {entraOnboardResult && entraOnboardResult.users.length > 0 && (
                  <div className="flex items-center gap-2 rounded-lg bg-blue-500/5 border border-blue-500/20 px-3 py-2">
                    <CheckCircle2 size={14} className="text-blue-500 shrink-0" />
                    <p className="text-xs text-blue-600">
                      Imported from Microsoft 365 directory. You can edit roles or remove users below.
                    </p>
                  </div>
                )}
                <div className="hidden grid-cols-[1fr_1fr_auto_auto] gap-2 text-xs font-medium text-text-muted sm:grid">
                  <span>Email</span>
                  <span>Display Name</span>
                  <span>Role</span>
                  <span />
                </div>
                {manualUsers.map((user, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
                    <Input
                      placeholder="user@example.com"
                      value={user.email}
                      onChange={(e) => updateManualUser(index, "email", e.target.value)}
                    />
                    <Input
                      placeholder="Full name"
                      value={user.displayName}
                      onChange={(e) => updateManualUser(index, "displayName", e.target.value)}
                    />
                    <select
                      value={user.role}
                      onChange={(e) => updateManualUser(index, "role", e.target.value)}
                      className="rounded-md border border-border bg-surface px-2 py-2 text-sm"
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeManualUser(index)}
                      disabled={manualUsers.length <= 1}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addManualUser}>
                  <Plus size={14} className="mr-1.5" />
                  Add User
                </Button>
                {!hasAdminUser && (
                  <div className="flex items-center gap-2 rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2">
                    <Shield size={14} className="text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-600">
                      At least one user with the <strong>Admin</strong> role is required.
                      The admin will complete the customer onboarding wizard on first login.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Step */}
      {isReviewStep && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review & Create Tenant</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border divide-y divide-border">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Company Name</span>
                <span className="text-sm text-text-primary">{name}</span>
              </div>
              {description && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-medium text-text-muted">Description</span>
                  <span className="text-sm text-text-primary max-w-xs truncate">{description}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Tier</span>
                <Badge variant="default">{tier}</Badge>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Issue Prefix</span>
                <Badge variant="secondary">{effectivePrefix}</Badge>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Brand Color</span>
                <div className="flex items-center gap-2">
                  <div
                    className="h-4 w-4 rounded border border-border"
                    style={{ backgroundColor: brandColor }}
                  />
                  <span className="text-sm text-text-primary">{brandColor}</span>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Azure Region</span>
                <Badge variant="secondary">
                  {({ uksouth: "UK South", ukwest: "UK West", westeurope: "West Europe", northeurope: "North Europe", eastus: "East US", eastus2: "East US 2", westus2: "West US 2" } as Record<string, string>)[azureRegion] || azureRegion}
                </Badge>
              </div>
              {websiteUrl && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-medium text-text-muted">Website</span>
                  <span className="text-sm text-text-primary max-w-xs truncate">{websiteUrl}</span>
                </div>
              )}
              {companyContext && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-medium text-text-muted">Company Context</span>
                  <span className="text-sm text-text-primary max-w-xs truncate">{companyContext.slice(0, 80)}{companyContext.length > 80 ? "..." : ""}</span>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-xs font-medium text-text-muted">Auth Provider</span>
                <span className="text-sm text-text-primary">
                  {AUTH_PROVIDERS.find((p) => p.id === authProvider)?.label}
                </span>
              </div>
              {authProvider === "entra_id" && (entraOnboardResult?.tenantId || entraConfig.tenantId) && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-medium text-text-muted">Entra Tenant ID</span>
                  <div className="flex items-center gap-2">
                    {entraOnboardResult && (
                      <CheckCircle2 size={12} className="text-green-500" />
                    )}
                    <span className="text-sm text-text-primary font-mono text-xs">
                      {(entraOnboardResult?.tenantId || entraConfig.tenantId).slice(0, 8)}...
                    </span>
                  </div>
                </div>
              )}
              {authProvider !== "local" && userMode === "manual" && (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-medium text-text-muted">Initial Users</span>
                  <span className="text-sm text-text-primary">
                    {manualUsers.filter((u) => u.email.trim()).length} user(s)
                  </span>
                </div>
              )}
            </div>

            {createError && (
              <p className="text-sm text-red-500">{createError}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          disabled={step === 1}
          onClick={handleBack}
        >
          <ArrowLeft size={14} className="mr-1.5" />
          Back
        </Button>
        {isReviewStep ? (
          <Button
            size="sm"
            disabled={isCreating}
            onClick={handleCreate}
          >
            <Building2 size={14} className="mr-1.5" />
            {isCreating ? "Creating..." : "Create Tenant"}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={!canProceed()}
            onClick={handleNext}
          >
            Next
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </CenterPanel>
  );
}
